import { format } from "date-fns";
import { useCallback, useState } from "react";

import { getLocalStorageValue, updateLocalStorage } from "@/hooks/useLocalStorage";

import {
  AI_CREDITS_CLOSE_TO_LIMIT_DISMISSED_KEY,
  AI_CREDITS_DAILY_LIMIT,
  AI_CREDITS_EXCEEDED_DISMISSED_KEY,
  AI_CREDITS_STORAGE_KEY,
} from "./constants";

type DailyCreditsState = {
  date: string;
  used: number;
};

export type UseDailyCreditsReturn = {
  showCloseToLimitBar: boolean;
  showExceededBar: boolean;
  increment: () => void;
  dismissCloseToLimitBar: () => void;
  dismissExceededBar: () => void;
};

export function useDailyCredits(): UseDailyCreditsReturn {
  const [todayKey] = useState(() => format(new Date(), "yyyy-MM-dd"));

  const [dailyCreditsUsed, setDailyCreditsUsed] = useState(() =>
    parseDailyCreditsUsed(getLocalStorageValue<DailyCreditsState | null>(AI_CREDITS_STORAGE_KEY, null), todayKey),
  );

  const [closeToLimitDismissedDate, setCloseToLimitDismissedDate] = useState(() =>
    getLocalStorageValue<string | null>(AI_CREDITS_CLOSE_TO_LIMIT_DISMISSED_KEY, null),
  );
  const [exceededDismissedDate, setExceededDismissedDate] = useState(() =>
    getLocalStorageValue<string | null>(AI_CREDITS_EXCEEDED_DISMISSED_KEY, null),
  );

  const creditsRemaining = Math.max(0, AI_CREDITS_DAILY_LIMIT - dailyCreditsUsed);
  const closeToLimit = creditsRemaining <= 3 && creditsRemaining > 0;
  const limitExceeded = creditsRemaining <= 0;

  const increment = useCallback(() => {
    const usedCredits = parseDailyCreditsUsed(
      getLocalStorageValue<DailyCreditsState | null>(AI_CREDITS_STORAGE_KEY, null),
      todayKey,
    );
    const nextUsedCredits = usedCredits + 1;
    updateLocalStorage(AI_CREDITS_STORAGE_KEY, { date: todayKey, used: nextUsedCredits });
    setDailyCreditsUsed(nextUsedCredits);
  }, [todayKey]);

  const dismissCloseToLimitBar = useCallback(() => {
    setCloseToLimitDismissedDate(todayKey);
    updateLocalStorage(AI_CREDITS_CLOSE_TO_LIMIT_DISMISSED_KEY, todayKey);
  }, [todayKey]);

  const dismissExceededBar = useCallback(() => {
    setExceededDismissedDate(todayKey);
    updateLocalStorage(AI_CREDITS_EXCEEDED_DISMISSED_KEY, todayKey);
  }, [todayKey]);

  return {
    showCloseToLimitBar: closeToLimit && closeToLimitDismissedDate !== todayKey,
    showExceededBar: limitExceeded && exceededDismissedDate !== todayKey,
    increment,
    dismissCloseToLimitBar,
    dismissExceededBar,
  };
}

function parseDailyCreditsUsed(stored: DailyCreditsState | null, todayKey: string): number {
  if (!stored) {
    return 0;
  }

  const { date, used } = stored;
  if (typeof date !== "string" || date !== todayKey) {
    return 0;
  }

  return typeof used === "number" && Number.isFinite(used) && used > 0 ? Math.floor(used) : 0;
}
